import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { Card, CardContent, CardHeader } from './ui/card';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { format, parseISO } from 'date-fns';
import DateRangeFilter from './DateRangeFilter';
import OpenRateGapChart from './OpenRateGapChart';

interface HistoryDataPoint {
    campaign_id: string;
    date: string;
    isp_stats: {
        [key: string]: {
            open_rate: number;
            estimated_open_rate: number;
        }
    };
}

interface IspPerformanceChartsProps {
    geo: string;
}

export default function IspPerformanceCharts({ geo }: IspPerformanceChartsProps) {
    const [historyData, setHistoryData] = useState<HistoryDataPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
    const [showEstimated, setShowEstimated] = useState(true);

    // Fetch history graph data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let historyUrl = `${API_BASE_URL}/api/segmentation/campaign-history-esp-breakdown?geo=${geo}&limit=7`;
                if (dateRange.start) historyUrl += `&start_date=${dateRange.start}`;
                if (dateRange.end) historyUrl += `&end_date=${dateRange.end}`;

                const historyRes = await fetch(historyUrl);
                if (historyRes.ok) {
                    const historyJson = await historyRes.json();
                    if (historyJson.history) {
                        setHistoryData(historyJson.history);
                    }
                }
            } catch (error) {
                console.error('Error fetching segmentation data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (geo) {
            fetchData();
        }
    }, [geo, dateRange]);

    // Calculate aggregated data for Gap Chart
    const gapChartData = useMemo(() => {
        if (!historyData.length) return [];

        const aggregates: Record<string, { sent: number; opened: number; est_opens: number }> = {
            google: { sent: 0, opened: 0, est_opens: 0 },
            microsoft: { sent: 0, opened: 0, est_opens: 0 },
            yahoo: { sent: 0, opened: 0, est_opens: 0 },
            other: { sent: 0, opened: 0, est_opens: 0 }
        };

        historyData.forEach(campaign => {
            Object.entries(campaign.isp_stats).forEach(([isp, stats]) => {
                const key = isp.toLowerCase();
                if (aggregates[key]) {
                    const sent = (stats as any).sent || 0;
                    const opened = (stats as any).opened || 0;
                    const est_rate = (stats as any).estimated_open_rate || (stats as any).open_rate || 0;

                    aggregates[key].sent += sent;
                    aggregates[key].opened += opened;
                    aggregates[key].est_opens += Math.round(sent * est_rate);
                }
            });
        });

        return Object.entries(aggregates).map(([name, data]) => {
            const actualRate = data.sent > 0 ? (data.opened / data.sent) * 100 : 0;
            const estimatedRate = data.sent > 0 ? (data.est_opens / data.sent) * 100 : 0;
            const gap = Math.max(0, estimatedRate - actualRate);

            return {
                name: name.charAt(0).toUpperCase() + name.slice(1),
                actual: parseFloat(actualRate.toFixed(1)),
                estimated: parseFloat(estimatedRate.toFixed(1)),
                gap: parseFloat(gap.toFixed(1))
            };
        });
    }, [historyData]);

    // Transform history data for chart
    const chartData = historyData.map(d => ({
        date: d.date,
        campaign: d.campaign_id, // tooltip info
        google: ((showEstimated ? d.isp_stats.google?.estimated_open_rate : d.isp_stats.google?.open_rate) || 0) * 100,
        microsoft: ((showEstimated ? d.isp_stats.microsoft?.estimated_open_rate : d.isp_stats.microsoft?.open_rate) || 0) * 100,
        yahoo: ((showEstimated ? d.isp_stats.yahoo?.estimated_open_rate : d.isp_stats.yahoo?.open_rate) || 0) * 100,
        other: ((showEstimated ? d.isp_stats.other?.estimated_open_rate : d.isp_stats.other?.open_rate) || 0) * 100
    }));

    const handleDateApply = (start: string | null, end: string | null) => {
        setDateRange({ start, end });
    };

    if (loading && historyData.length === 0) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-[300px] bg-gray-200 rounded-lg"></div>
            </div>
        );
    }

    if (historyData.length === 0) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Date Filter */}
            <DateRangeFilter onApply={handleDateApply} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Trend Chart */}
                <Card className="col-span-1 lg:col-span-2 shadow-md border-gray-300">
                    <CardHeader className="bg-gradient-to-r from-gray-50 to-blue-50 flex flex-row items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold">📈 ISP Performance Trends (Last 7 Sends)</h2>
                            <p className="text-xs text-gray-500">
                                {showEstimated ? "Estimated Final Open Rate % (72h Projection)" : "Actual Unique Open Rate % (Current Snapshot)"}
                            </p>
                        </div>
                        <div className="flex items-center space-x-2 bg-white p-1 rounded-lg border border-gray-200">
                            <button
                                onClick={() => setShowEstimated(false)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${!showEstimated ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Actual
                            </button>
                            <button
                                onClick={() => setShowEstimated(true)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${showEstimated ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Estimated
                            </button>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(val) => format(parseISO(val), 'MMM dd')}
                                        stroke="#9ca3af"
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis unit="%" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                        labelFormatter={(label) => format(parseISO(label as string), 'MMM dd, yyyy')}
                                        formatter={(value: any) => [`${Number(value).toFixed(1)}%`]}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                    <Line type="monotone" dataKey="google" name="Google" stroke="#4285F4" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="microsoft" name="Microsoft" stroke="#00A4EF" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="yahoo" name="Yahoo" stroke="#6001D2" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="other" name="Other" stroke="#6b7280" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Gap Chart */}
                {gapChartData.length > 0 && <OpenRateGapChart data={gapChartData} />}
            </div>
        </div>
    );
}
